import { useCallback, useEffect, useState } from 'react';
import axios from '../config/api';
import {
  downloadWeeklySettlementExcel,
  type WeeklySettlementResponse,
} from '../utils/downloadWeeklySettlementExcel';

export function useWeeklySettlementReport(selectedCafe: string) {
  const [data, setData] = useState<WeeklySettlementResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [weekStart, setWeekStart] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {};
      if (selectedCafe) params.cafe_id = selectedCafe;
      if (weekStart) params.week_start = weekStart;
      const { data: res } = await axios.get<WeeklySettlementResponse>(
        '/api/reports/weekly-settlement',
        { params }
      );
      setData(res);
      if (!weekStart && res.week_start) {
        setWeekStart(res.week_start);
      }
    } catch (err: unknown) {
      const message =
        (err as { response?: { data?: { error?: string } } })?.response?.data?.error ||
        (err as Error).message ||
        'Failed to load';
      setError(message);
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [selectedCafe, weekStart]);

  useEffect(() => {
    load();
  }, [load]);

  const downloadExcel = useCallback(() => {
    if (!data) return;
    const date = data.week_start;
    downloadWeeklySettlementExcel(
      `weekly-settlement-${date}.xlsx`,
      data,
      !selectedCafe
    );
  }, [data, selectedCafe]);

  const netPayable = data?.totals.net_payable_jod ?? 0;
  const weekLabel =
    data?.week_start && data?.week_end
      ? `${data.week_start} → ${data.week_end} (${data.timezone})`
      : 'Sun–Sat (Jordan)';

  const subtitle = loading
    ? 'Loading…'
    : error
      ? error
      : `Default: last complete week. ${weekLabel}. Includes Qahwahub credit subsidy; cash at café excluded from bank transfer.`;

  return {
    loading,
    error,
    data,
    weekStart,
    setWeekStart,
    netPayable,
    subtitle,
    downloadExcel,
    downloadDisabled: loading || !!error || !data,
    reload: load,
  };
}
